/* ============================================================================
   TASSE 3D — preview cilindrica con Three.js (punto 8 de la cola)
   ----------------------------------------------------------------------------
   La taza no tiene mockup fotografico: las 8 fotos de la tienda llevan ya un
   diseno impreso, y varias salen con manos o con varias tazas. En vez de
   buscar una foto perfecta, el cliente ve su taza EN 3D y la gira con el dedo.

   El editor sigue siendo PLANO — el wrap de 19 x 9 cm — y el 3D es solo un
   espejo en vivo de ese plano. Esto no es un detalle de implementacion, es la
   regla de produccion: el archivo de impresion es SIEMPRE el wrap plano sin
   deformar. Aqui no se genera nada que vaya a la imprenta.

   POR QUE GEOMETRIA POR CODIGO Y NO UN MODELO GLTF: un modelo decente pesa
   varios Mo, hay que alojarlo, y encima habria que confiar en que sus medidas
   son las de la taza real. Un torno (LatheGeometry) sobre un perfil escrito en
   CENTIMETROS da la taza exacta de Franklin — Ø 8,2 x 9,5 cm — en unas pocas
   lineas y cero descargas.

   POR QUE NO OrbitControls: el brief pide rotacion horizontal, un poco de
   vertical, y NADA de zoom ni de voltear la taza. Con OrbitControls habria que
   traerse un segundo archivo del CDN y despues desactivarle medio
   comportamiento. Cuarenta lineas de arrastre propio hacen justo lo pedido y
   se llevan bien con el dedo.
   ========================================================================== */

const TASSE3D = (() => {
'use strict';

/* Medidas REALES en cm (PRODUITS.md). La escena trabaja en centimetros: asi
   el perfil se lee como el plano de la taza y no hay factores magicos. */
const R_EXT   = 4.1;      // radio exterior (Ø 8,2)
const HAUTEUR = 9.5;
const PAROI   = 0.35;     // grosor de la pared y del borde
const WRAP_CM = { w: 19, h: 9 };   // zona imprimible que envuelve el cuerpo

/* La circunferencia real es π × 8,2 = 25,8 cm y el wrap solo mide 19: la
   impresion NO da la vuelta entera, deja un hueco donde va el asa. Reproducir
   ese hueco es lo que hace que el preview no mienta. */
const CIRCONFERENCE = Math.PI * R_EXT * 2;
/* El Taller puede pedir 22 × 9 en vez de 19 × 9 (fijarWrap). El Studio del
   cliente no lo llama nunca, así que para él todo sigue en 19 × 9. */
let wrapW = WRAP_CM.w, wrapH = WRAP_CM.h;
let ARC_WRAP = 2 * Math.PI * (wrapW / CIRCONFERENCE);

/* Cada cambio del diseno sube la textura entera a la GPU. En un movil eso es
   lo que hace que el 3D vaya « con retraso »: 1520 x 720 son 1,1 millones de
   pixeles por subida. A la mitad de lado se ve igual en una pantalla de 340
   px y cuesta la cuarta parte. */
const esMovil = () => Math.min(innerWidth, innerHeight) < 820
              || /iPhone|iPad|Android/i.test(navigator.userAgent);
const TEX_W = esMovil() ? 1024 : 1520;
const TEX_H = Math.round(TEX_W * 9 / 19);
const DEBOUNCE = 70;

/* ============================================================================
   LA MALLA DE VERDAD — GLB generado, banda de impresión calculada
   ----------------------------------------------------------------------------
   Franklin: « si Higgsfield puede producir un asset 3D real, prefiero esa
   arquitectura ». Puede: `tripo_3d` devuelve un GLB. Y una taza reconstruida
   tiene lo que un torno no da — el borde con su grosor de verdad, el interior,
   el pie, y un asa que nace de la pared en vez de estar apoyada en ella.

   Pero un GLB reconstruido NO sabe cuánto mide. Sus coordenadas son las que
   quiso el generador, y esta herramienta existe para decir « 20 cm de banda,
   2,9 de margen junto al asa ». Así que el modelo se MIDE al cargarlo:

     · la altura del cuerpo → escala, para que sean 9,5 cm;
     · el radio del cuerpo, por la mediana de los vértices a media altura —el
       asa es minoría y no mueve una mediana—, para comprobar el Ø 8,2;
     · el ángulo del asa → giro, para dejarla donde el resto del código la
       espera (+X).

   Y LA BANDA IMPRESA NO SALE DE LOS UV DEL GLB. Los UV de una malla
   reconstruida son un atlas cualquiera: pegar ahí un diseño de 20 × 9 cm sería
   volver a no saber dónde cae. La banda sigue siendo su propio cilindro
   —arco = 2π · (ancho/contorno), alto = wrapH, centrado enfrente del asa—
   apoyado sobre el radio MEDIDO del modelo. El GLB pone el realismo; la
   geometría calculada pone los centímetros.

   Si el GLB no está o no carga, se construye la taza de siempre. Un visor que
   depende de una descarga es un visor que un día no abre.
   ========================================================================== */
/* LA MALLA MANDA, Y LA CALCULADA ES LA RED. El estampado ya no se apoya en una
   funda cilíndrica: va proyectado en el material de la propia taza, así que la
   pared ondulada de una malla reconstruida deja de ser un problema. Si el GLB
   no está, no carga o el navegador no puede con él, se construye la taza de
   siempre — que sigue siendo exacta — y nadie se queda sin visor. */
/* ══════════════════ LA MALLA GENERADA SE QUEDA FUERA ═══════════════════
   Franklin, viéndola: « la taza que se ve ahora tiene una especie de parte
   inferior, base marcada, pie, y eso no es la taza correcta… quiero una taza
   clásica blanca estándar de 9 oz, sin pedestal, sin base gruesa visible ».

   Y tiene razón: el GLB lo generó una IA a partir de una descripción, y le
   puso una pestaña decorativa en la base que ninguna taza de sublimación de 9
   onzas tiene. Calibrar el estampado sobre una silueta que no es la del
   producto es calibrar sobre algo que no existe.

   La taza construida aquí —torno de revolución con su perfil— es EXACTA por
   construcción: Ø 8,2 × 9,5 cm, pared de 3 mm, borde con grosor, chaflán de
   base y nada más. Es la silueta clásica que él describe. Además pesa unos
   4 000 triángulos en vez de 78 000, así que el visor va más suelto.

   El GLB sigue ahí y se puede pedir con `?glb` en la dirección, o desde la
   consola con `TASSE3D.fijarModelo('assets/modelos3d/taza.glb')` seguido de
   `TASSE3D.cargarModeloTaza()`. No se borra nada: deja de ser lo que se
   enseña por defecto. */
let MODELO_URL = /[?&]glb\b/.test(typeof location !== 'undefined' ? location.search : '')
  ? 'assets/modelos3d/taza.glb' : null;
const URL_GLTF = 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js';
let modeloTaza = null, modeloEnCurso = null, modeloRadio = null, modeloAngulo = null;
let modeloRadioMax = null;

function fijarModelo(url) { MODELO_URL = url || MODELO_URL; }

function cargarGLTFLoader() {
  if (typeof THREE !== 'undefined' && THREE.GLTFLoader) return Promise.resolve(true);
  return new Promise(res => {
    const sc = document.createElement('script');
    sc.src = URL_GLTF; sc.async = true;
    sc.onload = () => res(!!(THREE && THREE.GLTFLoader));
    sc.onerror = () => res(false);
    document.head.appendChild(sc);
  });
}

/**
 * Mide el modelo y lo deja EN CENTÍMETROS, de pie, con el asa en el hueco.
 *
 * Todo se hornea en los vértices: al acabar, la coordenada local de cada malla
 * ES la coordenada de la taza —eje Y, apoyada en y = 0, Ø 8,2 × 9,5 cm— y el
 * shader puede leerla directamente para proyectar el estampado. Si se dejara
 * colocada con `scale`/`position`/`rotation` de sus padres, esas coordenadas
 * serían otras y el dibujo caería en cualquier sitio.
 *
 * Y se parte de `matrixWorld` de cada malla, no de la geometría cruda: un GLB
 * trae sus propios nodos con su escala y su giro, y saltárselos fue lo que me
 * dejó el estampado apretado en una franja — la taza medía otra cosa de la que
 * yo creía.
 */
function ajustarModelo(raiz) {
  raiz.updateMatrixWorld(true);
  const mallas = [];
  raiz.traverse(n => { if (n.isMesh && n.geometry) mallas.push(n); });
  if (!mallas.length) return null;

  const caja = new THREE.Box3().setFromObject(raiz);
  const tam = new THREE.Vector3(); caja.getSize(tam);
  const cen = new THREE.Vector3(); caja.getCenter(cen);
  if (!(tam.y > 0)) return null;
  const ky = HAUTEUR / tam.y;

  /* EL EJE NO ES EL CENTRO DE LA CAJA, Y AQUÍ ESTABA EL DESCENTRADO.
     La caja envolvente incluye el asa, que tira del centro un centímetro largo
     hacia su lado. Tomando ese punto como eje del cilindro, los ángulos salen
     sesgados: el estampado queda más cerca del asa por un lado que por el
     otro — exactamente lo que Franklin ve.

     El eje del CUERPO se saca aparte, y por iteración: se parte del centro de
     la caja, se miden los radios a media altura, se tira el cuarto más lejano
     —que es el asa— y se recalcula el centro con lo que queda. Tres vueltas y
     el eje se posa en el del cilindro, mire donde mire el asa. */
  const v = new THREE.Vector3();
  const muestras = [];
  mallas.forEach(m => {
    const pos = m.geometry.attributes.position;
    const paso = Math.max(1, Math.floor(pos.count / 8000));
    for (let i = 0; i < pos.count; i += paso) {
      v.fromBufferAttribute(pos, i).applyMatrix4(m.matrixWorld);
      if (Math.abs(v.y - cen.y) > tam.y * 0.15) continue;
      muestras.push({ x: v.x, z: v.z });
    }
  });
  if (muestras.length < 50) return null;

  let ejeX = cen.x, ejeZ = cen.z;
  for (let vuelta = 0; vuelta < 3; vuelta++) {
    const rs = muestras.map(p => Math.hypot(p.x - ejeX, p.z - ejeZ)).sort((a, b) => a - b);
    const corte = rs[Math.floor(rs.length * 0.75)];
    let sx2 = 0, sz2 = 0, n3 = 0;
    muestras.forEach(p => {
      if (Math.hypot(p.x - ejeX, p.z - ejeZ) <= corte) { sx2 += p.x; sz2 += p.z; n3++; }
    });
    if (!n3) break;
    ejeX = sx2 / n3; ejeZ = sz2 / n3;
  }

  const radios = [], puntas = [];
  muestras.forEach(p => {
    const r = Math.hypot(p.x - ejeX, p.z - ejeZ);
    radios.push(r);
    puntas.push({ r, a: Math.atan2(p.z - ejeZ, p.x - ejeX) });
  });
  radios.sort((a, b) => a - b);
  const r0 = radios[Math.floor(radios.length * 0.45)];   // la pared, sin el asa
  if (!(r0 > 0)) return null;
  const kx = R_EXT / r0;                    // Ø 8,2 cm de verdad

  /* El asa: lo que sobresale más de un 25 % del radio del cuerpo. Su ángulo
     medio se promedia en vectores — ahí sí vale, porque es un arco corto. */
  let sx = 0, sz = 0, n2 = 0;
  puntas.forEach(p => { if (p.r > r0 * 1.25) { sx += Math.cos(p.a); sz += Math.sin(p.a); n2++; } });
  const anguloAsa = n2 > 20 ? Math.atan2(sz, sx) : null;
  modeloAngulo = anguloAsa == null ? null : +(anguloAsa * 180 / Math.PI).toFixed(1);

  /* El asa va DENTRO del hueco sin imprimir: en una taza de verdad el dibujo
     no da la vuelta entera precisamente por ella. */
  const hueco = anguloCentroBanda() + Math.PI;
  const giro = anguloAsa == null ? 0 : (anguloAsa - hueco);

  const S = new THREE.Matrix4().makeScale(kx, ky, kx);
  const T = new THREE.Matrix4().makeTranslation(-ejeX * kx, -caja.min.y * ky, -ejeZ * kx);
  const R = new THREE.Matrix4().makeRotationY(giro);
  const base = new THREE.Matrix4().multiplyMatrices(R, new THREE.Matrix4().multiplyMatrices(T, S));

  const g = new THREE.Group();
  mallas.forEach(m => {
    const geo = m.geometry.clone();
    geo.applyMatrix4(new THREE.Matrix4().multiplyMatrices(base, m.matrixWorld));
    geo.computeVertexNormals();
    const malla = new THREE.Mesh(geo, materialCeramique());
    malla.castShadow = true; malla.receiveShadow = true;
    g.add(malla);
  });
  modeloRadio = R_EXT;
  modeloRadioMax = radios[Math.min(radios.length - 1, Math.floor(radios.length * 0.92))] * kx;
  g.userData.anguloAsa = anguloAsa;
  /* Comprobación: al acabar, la taza tiene que medir lo que dice medir. */
  const fin = new THREE.Box3().setFromObject(g);
  const t2 = new THREE.Vector3(); fin.getSize(t2);
  g.userData.medidas = { alto: +t2.y.toFixed(2), baseY: +fin.min.y.toFixed(2),
                         radio: +modeloRadio.toFixed(2) };
  return g;
}

function cargarModeloTaza() {
  if (!MODELO_URL) return Promise.resolve(null);
  if (modeloTaza) return Promise.resolve(modeloTaza);
  if (modeloEnCurso) return modeloEnCurso;
  modeloEnCurso = cargarGLTFLoader().then(ok => {
    if (!ok) return null;
    return new Promise(res => {
      new THREE.GLTFLoader().load(MODELO_URL,
        gltf => { try { res(ajustarModelo(gltf.scene)); } catch (e) { res(null); } },
        undefined,
        () => res(null));
    });
  }).then(m => { modeloTaza = m; return m; }).catch(() => null);
  return modeloEnCurso;
}

let THREE_OK = null;
let ren = null, scene = null, cam = null, groupe = null, deco = null;
let texture = null, lienzo = null;
let conteneur = null, cv = null;
let peindre = null;                // callback que pinta el wrap plano
let actif = false, visible = true, hayQueRender = true;
let bucle = null, tempo = null, obs = null;

/* Angulos de camara. La taza gira sobre si misma; la camara se queda quieta y
   lo que gira es el grupo, que es mas barato y mas facil de limitar.

   El azimut de partida NO es cero. Con el hueco de impresion en su sitio real
   —pegado al asa— el centro del diseno queda enfrente del asa, asi que ver el
   diseno centrado y ver el asa a la vez es imposible: en una taza de verdad
   tampoco se puede. Se elige el diseno, que es lo que el cliente viene a
   comprobar; el asa aparece en cuanto arrastra. */
let azimut = Math.PI / 2, elevacion = 0.12;
/* Acercar/alejar con topes: ni pegarse al esmalte ni perder la taza de vista. */
let zoom = 1;
const ZOOM_MIN = 0.62, ZOOM_MAX = 1.9;
const ELEV_MIN = -0.35, ELEV_MAX = 0.5;

/* ---------------------------------------------------------- WebGL ------- */
/* Solo el aparato, sin mirar si Three esta cargado: la decision de bajar la
   libreria depende de esto, asi que no puede depender de ella. */
function disponible() {
  if (THREE_OK !== null) return THREE_OK;
  try {
    const c = document.createElement('canvas');
    THREE_OK = !!(c.getContext('webgl') || c.getContext('experimental-webgl'));
  } catch (e) { THREE_OK = false; }
  return THREE_OK;
}

/* Three.js son ~600 ko. Cargarlo siempre significa que un cliente que compra
   una CAMISETA paga esa descarga por un 3D que no va a ver nunca — y por un
   hotspot eso son segundos de pantalla en blanco. Se baja BAJO DEMANDA, solo
   cuando se abre un producto con preview3D, y el editor plano no espera. */
const URL_THREE = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
let _promesaThree = null;

function chargerThree() {
  if (typeof THREE !== 'undefined') return Promise.resolve(true);
  if (_promesaThree) return _promesaThree;
  _promesaThree = new Promise(res => {
    const sc = document.createElement('script');
    sc.src = URL_THREE; sc.async = true;
    sc.onload  = () => res(typeof THREE !== 'undefined');
    sc.onerror = () => { console.warn('[EDISAMP] Three.js indisponible'); res(false); };
    document.head.appendChild(sc);
  });
  return _promesaThree;
}

/* ------------------------------------------------------- geometria ------ */
/* Perfil de la taza, en cm, leido de abajo a arriba por dentro y bajando por
   fuera. LatheGeometry lo hace girar sobre el eje Y. */
/* LAS MARCAS DEL TORNO — la textura TACTIL, en la GEOMETRIA.
   Franklin lo separo bien: textura visual es dibujo y color, lisa al tacto;
   tactil son «relieves, estrias e imperfecciones del barro que le dan caracter
   artesanal».
   Se intento primero con un mapa de normales y este Three NO lo aplicaba: se
   probo a 0,55 y a 6, en el barniz, sin entorno y con luz dura de frente, y el
   contraste local salia identico —7,728— con relieve 0 y con relieve 3,5. O
   sea que no era que se notara poco: no llegaba al render.
   Asi que el relieve va donde no puede ignorarse: en la pared. La pared deja
   de ser un cilindro perfecto y ondula con la altura, que es exactamente lo
   que deja el torno. Se ve en el sombreado Y en la silueta, y no depende de
   ningun mapa.
   La onda SIEMPRE resta, nunca suma: asi el radio maximo sigue siendo R_EXT y
   la pared no puede atravesar la funda del estampado, que va justo encima. */
const ONDA_AMPL = 0.032;      // cm — 0,3 mm, lo que mide una marca de torno

/* Tres periodos distintos. Con un seno solo, la taza parece un muelle. */
function ondaPared(y) {
  const s = 0.55 * Math.sin(y * 3.1 + 0.4)
          + 0.30 * Math.sin(y * 7.3 + 1.9)
          + 0.15 * Math.sin(y * 13.1 + 3.1);
  return (s + 1) / 2;                       // 0..1
}
const radioPared = y => R_EXT - ONDA_AMPL * ondaPared(y);

/* ══════════ EL PERFIL, PUNTO POR PUNTO — Y SIN ESCALÓN ABAJO ══════════════
   Franklin lo dibujó así y no hay forma más clara de decirlo:

       │              │
       │     y NO     │
       ╰──            ├─
                      ╰──

   O sea: la pared baja seguida y se curva UNA vez para entrar en la base. Ni
   un anillo, ni un segundo tramo, ni una pestaña.

   Lo que había era un chaflán de un solo segmento —de radio 4,10 a 3,85 en
   tres milímetros—, que es una arista recta a 40°. Con la luz de estudio esa
   arista coge un brillo duro y se lee como un escalón aunque no lo sea.
   Ahora es un cuarto de círculo de verdad, repartido en ocho puntos: la pared
   entra en el fondo sin que haya en ningún sitio un cambio brusco de dirección.

   Y arriba, el borde: estaba INSETADO —la boca salía 1,8 mm más estrecha que
   la pared—, que es de taza de cerámica artesanal. En una taza de sublimación
   el canto es liso y a ras. */
const RADIO_BASE = 0.35;      // cm — el redondeo con el que la pared entra al fondo

function profil() {
  const rInt = R_EXT - PAROI;
  const p = [];
  const V = (x, y) => p.push(new THREE.Vector2(x, y));

  /* ---- por dentro: fondo, subida y borde ---- */
  V(0.001, PAROI);            // centro del fondo interior
  V(rInt - 0.30, PAROI);
  V(rInt, PAROI + 0.30);      // redondeo del fondo por dentro
  V(rInt, HAUTEUR - 0.25);
  /* El canto, a ras de la pared y redondeado: dos puntos bastan para que la
     luz resbale en vez de cortarse. */
  V(rInt + 0.06, HAUTEUR - 0.06);
  V(R_EXT - 0.04, HAUTEUR);

  /* ---- la pared exterior, de arriba abajo ----
     LatheGeometry interpola RECTO entre vertices, asi que la onda del torno
     necesita muchos puntos o seria un zigzag. */
  const Y0 = HAUTEUR - 0.02, Y1 = RADIO_BASE, N = 64;
  for (let i = 0; i <= N; i++) {
    const y = Y0 + (Y1 - Y0) * (i / N);
    V(radioPared(y), y);
  }

  /* ---- y la curva que entra en la base ----
     Un cuarto de círculo de RADIO_BASE: la tangente de arriba coincide con la
     pared (vertical) y la de abajo con el fondo (horizontal), así que no queda
     ninguna arista que pueda leerse como un pie. */
  const rPared = radioPared(RADIO_BASE);
  for (let i = 1; i <= 8; i++) {
    const a = (Math.PI / 2) * (i / 8);          // 0 = pared, π/2 = fondo
    V(rPared - RADIO_BASE * (1 - Math.cos(a)),
      RADIO_BASE * (1 - Math.sin(a)));
  }
  V(0.001, 0.0);              // fondo plano, apoyado en la mesa
  return p;
}

/* GRANO DEL ESMALTE. Franklin: « con textura real como si fuera una taza ».
   Una taza real NO tiene un brillo uniforme: el esmalte se acumula y se afina
   al hornearse, asi que el reflejo se corta en manchas anchas y muy suaves.
   Eso es lo que el ojo lee como ceramica, y una rugosidad constante —lo que
   habia— lee como plastico por muy bien iluminado que este.
   Se pinta en un canvas, no se descarga: manchas grandes de baja frecuencia
   mas un ruido fino de poca amplitud. Se usa como mapa de RUGOSIDAD, no como
   relieve: un normal map sacado de ruido daria papel de lija, que es peor que
   no tener nada. */
let _grain = null;
function grainEmail() {
  if (_grain) return _grain;
  const N = 256;
  const c = document.createElement('canvas'); c.width = c.height = N;
  const x = c.getContext('2d');
  x.fillStyle = '#b4b4b4'; x.fillRect(0, 0, N, N);

  /* Manchas anchas: el esmalte mas grueso brilla mas (mas oscuro en el mapa
     de rugosidad), el mas fino menos. */
  for (let i = 0; i < 90; i++) {
    const px = Math.random() * N, py = Math.random() * N;
    const r = 14 + Math.random() * 46;
    const clair = Math.random() < 0.5;
    const g = x.createRadialGradient(px, py, 0, px, py, r);
    g.addColorStop(0, clair ? 'rgba(255,255,255,.20)' : 'rgba(0,0,0,.16)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = g; x.beginPath(); x.arc(px, py, r, 0, 7); x.fill();
  }

  /* Y un ruido fino, muy flojo: quita la sensacion de degradado perfecto. */
  const d = x.getImageData(0, 0, N, N);
  for (let i = 0; i < d.data.length; i += 4) {
    const n = (Math.random() - 0.5) * 12;
    d.data[i] += n; d.data[i+1] += n; d.data[i+2] += n;
  }
  x.putImageData(d, 0, 0);

  _grain = new THREE.CanvasTexture(c);
  _grain.wrapS = _grain.wrapT = THREE.RepeatWrapping;
  _grain.repeat.set(3, 2);
  return _grain;
}

/* Esmalte = cuerpo mate cubierto por una capa de vidrio. Eso es literalmente
   lo que describe `clearcoat`, y es la diferencia entre « brillante » y
   « esmaltado »: el cuerpo dispersa suave y ENCIMA hay un reflejo nitido y
   fino. Con MeshStandardMaterial las dos cosas eran una sola y habia que
   elegir entre plastico brillante o yeso mate. */
function materialCeramique() {
  return new THREE.MeshPhysicalMaterial({
    /* Un pelo por debajo del blanco papel: una taza real nunca llega a 255. */
    color: 0xe4e1dc,
    roughness: 0.38, roughnessMap: grainEmail(),
    metalness: 0.0,
    /* Sin revelado, un clearcoat de 1 con rugosidad 0.055 clava el reflejo en
       255 y se pierde el dibujo del brillo. 0.9 / 0.075 deja el mismo esmalte
       con la mancha todavia dentro de rango. */
    clearcoat: 0.9, clearcoatRoughness: 0.09,
    envMapIntensity: 0.5,
    side: THREE.DoubleSide,
  });
}


/* ============================================================================
   EL ESTAMPADO, SOBRE LA MALLA — proyección cilíndrica en el propio material
   ----------------------------------------------------------------------------
   Franklin: « no quiero que el GLB quede apagado; cierra la arquitectura ».

   El problema con una malla reconstruida es que NO es un cilindro: su pared
   ondula un par de milímetros. Apoyarle encima una funda cilíndrica —lo que
   funciona perfecto con la taza calculada— deja el estampado metido dentro del
   barro por un lado y flotando por el otro. Se ve, y es justo lo que él vio.

   La solución no es mover la funda: es que no haya funda. El dibujo se
   proyecta EN EL MATERIAL de la taza, con coordenadas cilíndricas calculadas
   por píxel:

       u = (ángulo − centro de la banda) / arco + ½
       v = (altura − base de la banda) / alto de la banda

   y sólo se pinta donde 0 ≤ u ≤ 1 y 0 ≤ v ≤ 1. Así la tinta cae sobre la
   superficie que HAY, siga la onda que siga: no puede flotar ni esconderse,
   porque no es un objeto aparte — es el color de esa pared.

   Lo que queda fuera se queda fuera solo, sin listas ni excepciones:
     · el asa, porque vive en el hueco sin imprimir (u < 0 o u > 1);
     · el interior, porque su normal mira hacia el eje y se descarta;
     · el borde y el pie, porque caen fuera del alto de la banda.

   Y el formato manda de verdad: 19, 20 y 22 cm dan arcos de 265°, 279° y 307°
   sobre el mismo contorno de 25,8 cm, así que cambiarlo cambia cuánta vuelta
   ocupa el dibujo y cuánto queda para el asa. Los uniformes se actualizan sin
   recompilar nada.

   `startAngle`/`endAngle` no son dos números sueltos: son centro ± arco/2, y
   el centro es el opuesto del asa. Se exponen en `zonaImprimible()` para poder
   comprobarlos desde fuera.
   ========================================================================== */

/** Centro del arco impreso, en el mismo convenio que `atan2(z, x)`. */
function anguloCentroBanda() {
  /* SE LEE EL VÉRTICE DEL MEDIO, NO LA MEDIA. Promediando las direcciones de
     todo el arco salía el ángulo CONTRARIO, y no por un fallo de cuentas: en
     un arco de más de 180° —el de 22 cm son 307°— la media vectorial apunta
     hacia el hueco, porque las dos puntas tiran cada una para su lado y lo que
     queda sin compensar es justo lo que falta. De ahí venían las vueltas que
     dimos con el asa: el hueco y la banda intercambiados.
     El vértice central del arco no tiene esa ambigüedad. */
  const seg = 24;
  const g = new THREE.CylinderGeometry(1, 1, 1, seg, 1, true,
    Math.PI * 1.5 - ARC_WRAP / 2, ARC_WRAP);
  const pos = g.attributes.position;
  const medio = seg >> 1;                 // el vértice del centro del arco
  const a = Math.atan2(pos.getZ(medio), pos.getX(medio));
  g.dispose();
  return a;
}

/** Dónde empieza y acaba la banda, en cm y en grados. Para comprobarlo. */
function zonaImprimible() {
  const centro = anguloCentroBanda();
  const gr = a => +(((a * 180 / Math.PI) % 360 + 360) % 360).toFixed(1);
  return {
    printWidthCm: wrapW, printHeightCm: wrapH,
    startAngle: gr(centro - ARC_WRAP / 2), endAngle: gr(centro + ARC_WRAP / 2),
    arcoGrados: +(ARC_WRAP * 180 / Math.PI).toFixed(1),
    verticalOffset: +(HAUTEUR / 2 - wrapH / 2).toFixed(2),
    circunferencia: +CIRCONFERENCE.toFixed(2),
  };
}

/* Los materiales con estampado proyectado: hay que poder tocarles los
   uniformes cuando cambia el formato, sin reconstruir la taza. */
const _matsProyectados = [];

function materialProyectado() {
  const mat = materialCeramique();
  const z = zonaImprimible();
  const datos = {
    uWrap:   { value: texture },
    uCentro: { value: anguloCentroBanda() },
    uArco:   { value: ARC_WRAP },
    uBase:   { value: HAUTEUR / 2 - wrapH / 2 },
    uAlto:   { value: wrapH },
  };
  mat.onBeforeCompile = shader => {
    Object.keys(datos).forEach(k => { shader.uniforms[k] = datos[k]; });
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>',
        '#include <common>\nvarying vec3 vTazaPos;\nvarying vec3 vTazaNor;')
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\nvTazaPos = position;\nvTazaNor = normal;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>',
        '#include <common>\nvarying vec3 vTazaPos;\nvarying vec3 vTazaNor;\n'
        + 'uniform sampler2D uWrap;\nuniform float uCentro;\nuniform float uArco;\n'
        + 'uniform float uBase;\nuniform float uAlto;')
      .replace('#include <map_fragment>', `
        #include <map_fragment>
        {
          /* Hacia fuera o hacia dentro: el interior de la taza no se imprime. */
          vec2 radial = normalize(vec2(vTazaPos.x, vTazaPos.z) + vec2(1e-6));
          float haciaFuera = dot(normalize(vec2(vTazaNor.x, vTazaNor.z) + vec2(1e-6)), radial);
          float ang = atan(vTazaPos.z, vTazaPos.x);
          float d = ang - uCentro;
          d = mod(d + 3.14159265, 6.28318531) - 3.14159265;
          float u = d / uArco + 0.5;
          float v = (vTazaPos.y - uBase) / uAlto;
          if (haciaFuera > 0.25 && u >= 0.0 && u <= 1.0 && v >= 0.0 && v <= 1.0) {
            /* Dos espejos, y los dos tienen nombre. La textura de lienzo se
               sube con flipY, o sea que su v ya va de abajo a arriba:
               restarla otra vez dejaba el dibujo cabeza abajo. Y el angulo
               crece al reves de como se lee el wrap visto desde fuera, de ahi
               el 1 menos u; sin el, las letras salian en espejo. */
            vec4 tinta = texture2D(uWrap, vec2(1.0 - u, v));
            /* La textura es sRGB y aquí se trabaja en lineal. */
            tinta.rgb = pow(tinta.rgb, vec3(2.2));
            diffuseColor.rgb = mix(diffuseColor.rgb, tinta.rgb, tinta.a);
          }
        }
      `);
    mat.userData.shader = shader;
  };
  mat.customProgramCacheKey = () => 'taza-proyectada';
  _matsProyectados.push({ mat, datos });
  return mat;
}

/** Al cambiar de formato, los uniformes siguen al arco nuevo. */
function refrescarProyeccion() {
  const centro = anguloCentroBanda();
  _matsProyectados.forEach(({ datos }) => {
    datos.uCentro.value = centro;
    datos.uArco.value = ARC_WRAP;
    datos.uBase.value = HAUTEUR / 2 - wrapH / 2;
    datos.uAlto.value = wrapH;
    datos.uWrap.value = texture;
  });
  marquer();
}

/* La funda del estampado, aparte para poder rehacerla al cambiar de formato. */
function geoFunda() {
  /* SOBRE LA PARED QUE HAY. Con la malla generada, el radio del cuerpo no es
     el del torno: se midió al cargarla y es ÉSE el que manda, o la banda
     flotaría a un milímetro de la taza o se metería dentro. El arco sigue
     saliendo de la cuenta de siempre —2π · (ancho del wrap / contorno)—, así
     que 19, 20 y 22 cm ocupan de verdad porciones distintas de la vuelta. */
  const rPared = (modeloRadioMax && modeloRadioMax > 1 ? modeloRadioMax
                : (modeloRadio && modeloRadio > 1 ? modeloRadio : R_EXT)) + 0.03;
  const geoDeco = new THREE.CylinderGeometry(
    rPared, rPared, wrapH, 72, 56, true,
    Math.PI * 1.5 - ARC_WRAP / 2, ARC_WRAP);
  {
    const pos = geoDeco.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const r = Math.hypot(x, z);
      if (r < 1e-6) continue;
      /* La funda se coloca luego en y = HAUTEUR/2; su y local + eso es la
         altura real sobre la que esta medida la onda. */
      const nuevoR = radioPared(y + HAUTEUR / 2) + 0.012;
      pos.setX(i, x / r * nuevoR);
      pos.setZ(i, z / r * nuevoR);
    }
    pos.needsUpdate = true;
    geoDeco.computeVertexNormals();
  }
  return geoDeco;
}

/* 19 × 9 o 22 × 9. Rehace sólo la funda: la taza, las luces y la cámara no
   se tocan. La textura sigue siendo el mismo lienzo; como la funda estira el
   lienzo entero sobre su arco, el dibujo sale a su medida real. */
function fijarWrap(w, h) {
  w = +w || WRAP_CM.w; h = +h || WRAP_CM.h;
  if (w === wrapW && h === wrapH) return false;
  wrapW = w; wrapH = h;
  ARC_WRAP = 2 * Math.PI * (wrapW / CIRCONFERENCE);
  /* El formato manda en la proyección igual que en la funda: cambia el arco y
     cambia cuánta vuelta ocupa el dibujo. */
  if (typeof refrescarProyeccion === 'function') refrescarProyeccion();
  if (deco && typeof THREE !== 'undefined') {
    const vieja = deco.geometry;
    deco.geometry = geoFunda();
    vieja.dispose();
    marquer();
  }
  return true;
}

function construireTasse() {
  const g = new THREE.Group();

  /* El cuerpo: la malla generada si está cargada, y si no el torno de siempre.
     Los dos se apoyan en y = 0 y miden 9,5 cm, así que todo lo que viene
     después —la banda, la sombra, la cámara— no se entera de cuál es. */
  let modeloClon = null;
  if (modeloTaza) {
    modeloClon = modeloTaza.clone(true);
    modeloClon.userData.anguloAsa = modeloTaza.userData.anguloAsa;
    /* El material lleva el estampado proyectado: la tinta es el color de la
       pared, no un objeto encima. Se crea uno por malla porque cada uno se
       compila con su shader. */
    modeloClon.traverse(n2 => {
      if (n2.isMesh) { n2.material = materialProyectado(); n2.castShadow = true; n2.receiveShadow = true; }
    });
    g.add(modeloClon);
    g.userData.glb = true;
  } else {
    const corps = new THREE.Mesh(
      new THREE.LatheGeometry(profil(), 72), materialCeramique());
    corps.geometry.computeVertexNormals();
    corps.castShadow = true;
    g.add(corps);
  }

  /* ---------------------------------------------------------------- asa ---
     Franklin: « cuando giro la taza, el asa desaparece de forma incorrecta o
     deja huecos visuales ». Mirándola en seis ángulos: gira bien —se esconde
     detrás del cuerpo cuando le toca, como en una taza de verdad— pero era
     PEQUEÑA y estaba mal pegada: un toro de 2,15 cm de radio flotando a 0,35
     cm de la pared, con los extremos al aire. De frente casi no se veía y de
     lado se notaba el despegue.

     Ahora: más grande (2,45 cm de radio, sección 0,58), un arco algo más
     cerrado, y CLAVADA en la pared — el centro del toro se mete 0,15 cm dentro
     del cuerpo, así que los dos extremos quedan enterrados en la cerámica y no
     hay junta que enseñar. Una taza de 9,5 cm con un asa de 2,45 de radio es
     la proporción de una 11 oz de verdad; la de antes era de juguete. */
  const anse = modeloTaza ? null : new THREE.Mesh(
    new THREE.TorusGeometry(2.45, 0.58, 18, 56, Math.PI * 1.22),
    materialCeramique());
  if (anse) {
  anse.rotation.z = -Math.PI * 0.61;
  anse.position.set(R_EXT - 0.15, HAUTEUR * 0.5, 0);
  /* El asa es la que hace la sombra reconocible: el hueco entre el asa y el
     cuerpo es lo que dice « esto es una taza » desde lejos. */
  anse.castShadow = true;
  anse.receiveShadow = true;
  g.add(anse);
  }

  /* La impresion: una funda cilindrica finisima justo sobre la pared, que
     cubre SOLO el arco del wrap y esta centrada enfrente del asa. */
  lienzo = document.createElement('canvas');
  lienzo.width = TEX_W; lienzo.height = TEX_H;
  texture = new THREE.CanvasTexture(lienzo);
  /* El dibujo se ve casi de canto en los bordes del cilindro, que es justo
     donde 4 muestras no bastan y el estampado se emborrona ahi. Se pide lo
     que la tarjeta pueda dar, con tope 8 para no pagarlo en un movil. */
  texture.anisotropy = Math.min(8,
    (ren && ren.capabilities && ren.capabilities.getMaxAnisotropy()) || 4);
  if ('colorSpace' in texture) texture.colorSpace = THREE.SRGBColorSpace;
  else if ('encoding' in texture) texture.encoding = THREE.sRGBEncoding;

  /* En Three, theta=0 mira a +Z (hacia el cliente) y theta=PI/2 a +X, que es
     donde va el asa. El wrap cubre 266 de los 360 grados: los 94 que faltan
     son el hueco sin imprimir, y ese hueco tiene que caer JUSTO en el asa. Por
     eso el arco se centra en el lado opuesto, 270 grados. Asi el cliente ve su
     diseno de frente y el blanco aparece solo al girar hacia el asa, que es lo
     que pasa con la taza de verdad. */
  /* La funda del estampado sigue la MISMA onda que la pared. Si se dejara
     cilindrica, la impresion flotaria sobre un barro ondulado y en el borde
     del arco se veria el escalon. Hacen falta segmentos en altura para poder
     seguirla: con uno solo no hay vertices que mover. */
  /* LA FUNDA SÓLO EXISTE PARA LA TAZA CALCULADA. Sobre la malla generada el
     estampado va proyectado en su material, así que una funda encima sería
     justo el problema que veníamos a quitar. Se crea igualmente —apagada—
     porque `fijarWrap` y el diagnóstico la usan como referencia del arco. */
  const geoDeco = geoFunda();
  deco = new THREE.Mesh(geoDeco, new THREE.MeshPhysicalMaterial({
    /* La tinta sublimada queda DENTRO del esmalte, no encima: el color se
       difumina un poco y el brillo que se ve es el del MISMO vidrio que cubre
       el resto de la taza. De ahi el clearcoat identico al del cuerpo — sin
       el, el dibujo parecia una pegatina mate pegada sobre una taza brillante,
       y el corte se notaba justo en el borde del arco. */
    map: texture,
    roughness: 0.44, roughnessMap: grainEmail(), metalness: 0.0,
    /* Mas suave que en la ceramica desnuda: sobre el dibujo, cada punto de
       brillo es color que el cliente deja de ver. Barrido medido — con 0.65 y
       env 0.50 la saturacion se queda en 0,114, la misma que antes de tocar
       nada, y el esmalte se sigue notando. */
    clearcoat: 0.65, clearcoatRoughness: 0.13,
    envMapIntensity: 0.33,
    transparent: true, side: THREE.DoubleSide,
  }));
  deco.position.y = HAUTEUR / 2;
  deco.visible = !modeloClon;
  g.add(deco);

  g.position.y = -HAUTEUR / 2;
  return g;
}

/* Sombra de contacto FALSA: un disco con un degradado radial. Un shadow map
   de verdad costaria otro paso de render en cada cuadro para una sombra que
   se ve durante medio segundo al girar. */
/* ══════════════ SOMBRA PROYECTADA DE VERDAD ═════════════════════════════
   Lo que separaba esta taza de una foto no era la forma —un torno da el
   cilindro exacto— ni el esmalte, que ya refleja un estudio. Era la SOMBRA.

   La de contacto es una mancha radial pintada: no cambia al girar la taza, no
   tiene el hueco del asa y no se alarga cuando la luz viene de lado. El ojo
   lee eso en menos de un segundo como « recorte pegado », y es exactamente lo
   que Franklin describe cuando dice que se ve básico.

   Una sombra calculada resuelve las tres cosas a la vez: sale de la geometría
   real, se deforma con la luz y deja ver el agujero del asa. Sobre la foto de
   una mesa es la diferencia entre una taza puesta ahí y una pegatina.

   Va sobre un `ShadowMaterial`: un plano que NO se dibuja y sólo recibe
   sombra. Sobre el fondo de la escena queda la sombra y nada más. */
function planSombra() {
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(60, 60),
    /* 0,18 y no 0,34: con la mancha de contacto encima, 0,34 pintaba un
       charco oscuro que se leía como una base. */
    new THREE.ShadowMaterial({ opacity: 0.18 }));
  m.rotation.x = -Math.PI / 2;
  m.position.y = -HAUTEUR / 2 - 0.02;
  m.receiveShadow = true;
  return m;
}

/* ══════════ LO QUE PARECÍA UN PEDESTAL ERAN DOS SOMBRAS ═══════════════════
   Franklin, tres veces: « tiene una base marcada, un pie, parece que la taza
   está colocada encima de una plataforma ». Y no era la geometría.

   Medida la silueta del render con el suelo escondido y con él:

     taza sola          ancho 376-387 px de arriba abajo — un cilindro recto
     con las sombras    ancho 491 px en el cuarto inferior, un +28 %

   Ese +28 % era esto: una mancha redonda pintada de **15 × 15 cm** debajo de
   una taza de 8,2 de diámetro. Sobresalía tres centímetros y medio por cada
   lado, y a la vista es exactamente una peana. Encima se le sumaba la sombra
   proyectada de verdad al 34 %.

   Ahora la mancha mide lo que mide el culo de la taza y sólo oscurece la línea
   de contacto; la sombra de verdad —la que dibuja el hueco del asa y hace que
   la taza esté apoyada y no flotando— se queda, pero a la mitad de fuerza. */
function ombreContact() {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const x = c.getContext('2d');
  /* El núcleo llega sólo al 30 % del radio: fuera de ahí ya es la sombra
     proyectada quien manda. */
  const d = x.createRadialGradient(64, 64, 4, 64, 64, 62);
  d.addColorStop(0, 'rgba(34,45,82,.26)');
  d.addColorStop(0.30, 'rgba(34,45,82,.10)');
  d.addColorStop(0.62, 'rgba(34,45,82,.02)');
  d.addColorStop(1, 'rgba(34,45,82,0)');
  x.fillStyle = d; x.fillRect(0, 0, 128, 128);
  const m = new THREE.Mesh(
    /* 9,6 cm: el diámetro de la taza más un dedo. Antes, 15. */
    new THREE.PlaneGeometry(9.6, 9.6),
    new THREE.MeshBasicMaterial({ map: new THREE.CanvasTexture(c),
      transparent: true, depthWrite: false }));
  m.rotation.x = -Math.PI / 2;
  m.position.y = -HAUTEUR / 2 - 0.02;
  return m;
}

/* ENTORNO REFLEJADO — lo que faltaba para que pareciera ceramica.
   Un material con roughness baja es BRILLANTE, y un brillo sin nada que
   reflejar no tiene donde mirarse: el motor solo puede responder con el
   especular de las luces directas, y eso se ve como plastico plano iluminado
   de mas. Justo lo que Franklin describio: « demasiada luz, demasiado basico ».

   La solucion normal es un HDR de estudio, pero eso son megas que descargar.
   Aqui se PINTA el estudio: un degradado equirectangular con el techo claro,
   una banda de softbox y el suelo oscuro. Pesa cero y da lo que importa — que
   el esmalte tenga un horizonte que reflejar y un degradado a lo largo del
   cilindro, que es lo que el ojo lee como « ceramica ».  */
function environnementStudio(renderer) {
  /* Al doble de lado: con clearcoat el reflejo es NITIDO, y a 512x256 se veian
     los escalones del degradado en el borde del esmalte. */
  const W = 1024, H = 512;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const x = c.getContext('2d');
  /* EL TECHO YA NO ES BLANCO PURO, Y ESA ERA LA CAUSA DE LA QUEMAZON.
     Franklin: « hay demasiada iluminación, cuando giro me daña los ojos ».
     Medido antes de tocar nada: el 89 % de los pixeles de la taza estaban a
     252 o mas, o sea RECORTADOS a blanco. Una taza blanca bajo un techo
     blanco a plena intensidad no tiene ningun sitio a donde ir.
     La solucion no es apagar el brillo —eso la deja mate— sino bajar el
     AMBIENTE y dejar las softbox intactas: asi el reflejo sigue existiendo,
     pero el resto de la taza recupera su degradado y se ve la forma. */
  const g = x.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0.00, '#f0eeea');   // techo del estudio, ya no 100 % blanco
  g.addColorStop(0.42, '#ddd8d0');
  g.addColorStop(0.50, '#c2bbb0');   // horizonte
  g.addColorStop(0.54, '#a49d93');
  g.addColorStop(1.00, '#6e6a63');   // suelo
  x.fillStyle = g; x.fillRect(0, 0, W, H);

  /* DOS softbox y no una. Con una sola, media vuelta de taza no tenia nada que
     reflejar y ese lado se veia muerto — parte de lo que Franklin llama
     « basico ». Con dos, de tamanos distintos y en lados opuestos, siempre hay
     un reflejo entrando o saliendo mientras gira, que es lo que hace que se
     lea como un objeto de verdad y no como un dibujo. */
  const softbox = (px, py, r, alpha) => {
    const s = x.createRadialGradient(px, py, r * 0.06, px, py, r);
    s.addColorStop(0,   'rgba(255,255,255,' + alpha + ')');
    s.addColorStop(0.45,'rgba(255,255,255,' + (alpha * 0.42) + ')');
    s.addColorStop(1,   'rgba(255,255,255,0)');
    x.fillStyle = s; x.beginPath(); x.arc(px, py, r, 0, 7); x.fill();
  };
  /* Se quedan a plena potencia A PROPOSITO: son el unico sitio de donde sale
     el destello del esmalte. Bajarlas tambien habria dejado la taza mate. */
  softbox(W * 0.28, H * 0.26, 250, 1.0);    // la principal
  softbox(W * 0.76, H * 0.34, 150, 0.55);   // la de relleno, mas pequena

  /* Una franja oscura bajo el horizonte: da al esmalte una linea de contraste
     que recorre el cilindro, y sin ella el brillo no tiene forma. */
  const b = x.createLinearGradient(0, H * 0.5, 0, H * 0.66);
  b.addColorStop(0, 'rgba(40,38,35,.30)');
  b.addColorStop(1, 'rgba(40,38,35,0)');
  x.fillStyle = b; x.fillRect(0, H * 0.5, W, H * 0.16);

  const tex = new THREE.CanvasTexture(c);
  tex.mapping = THREE.EquirectangularReflectionMapping;
  try {
    /* PMREM difumina el entorno segun la rugosidad: sin el, el reflejo sale
       como un espejo duro y canta mas que no tenerlo. */
    const pm = new THREE.PMREMGenerator(renderer);
    pm.compileEquirectangularShader();
    const rt = pm.fromEquirectangular(tex);
    tex.dispose(); pm.dispose();
    return rt.texture;
  } catch (e) { return tex; }
}

/* Con entorno, las luces directas bajan mucho: antes tenian que hacer TODO el
   trabajo y por eso quemaban. */
function lumieres() {
  const l = [];
  /* Todas al 80 % de lo que habia: con el ambiente ya bajado, mantenerlas
     habria devuelto la taza al recorte por otro camino. */
  /* ══════ POR QUÉ SE VEÍA « BÁSICA », Y ESTÁ MEDIDO ══════════════════════
     Volcado el histograma de la cerámica: el 93 % de los píxeles caían en la
     franja 208-234. Veintiséis niveles de 255. Recortado sólo el 1,1 %, así
     que no era quemazón — era FALTA DE MODELADO: la taza estaba iluminada por
     igual en toda su vuelta, y una superficie sin degradado se lee como un
     dibujo plano por muy bien hecha que esté la geometría. Eso es exactamente
     lo que Franklin llama básico.

     Una taza real fotografiada recorre todo el rango: el lado opuesto a la
     luz baja a 130-160, el medio tono ronda 200 y el especular sube a 250.
     Para conseguirlo hay que BAJAR el ambiente y SUBIR la clave: el ambiente
     rellena por igual y es justo lo que aplasta el degradado.
     El envMap sube también, porque el reflejo del estudio es lo que dibuja la
     curvatura a lo largo del cilindro.

     PRIMER INTENTO, PASADO DE ROSCA: subí la clave a 0.66 y el envMap a 0.9 y
     el 59 % de la taza salió recortado a blanco, media 246. Subir todo no es
     dar contraste, es quemar. Lo que da modelado es la PROPORCIÓN entre clave
     y ambiente, no la suma — y la suma hay que bajarla, no subirla. También
     baja el albedo de la cerámica: partiendo de 0xf0efec no queda sitio por
     arriba para un especular, y sin especular no hay esmalte. */
  l.push(new THREE.HemisphereLight(0xffffff, 0xd9d4cc, 0.07));
  const key = new THREE.DirectionalLight(0xffffff, 0.50);
  key.position.set(-6, 12, 9);
  /* La que proyecta. Sólo una: dos sombras cruzadas delatan el 3D tanto como
     no tener ninguna — en una mesa hay una fuente dominante y punto.
     La cámara de sombra se ajusta a la taza y no más: repartir 2048 px sobre
     un volumen enorme da el borde dentado que se ve en los mockups malos. */
  key.castShadow = true;
  /* 1024 y no 2048: cuatro veces menos píxeles de profundidad que calcular, y
     con `radius 3` el borde ya iba difuminado — la diferencia no se ve, el
     coste sí se notaba. */
  key.shadow.mapSize.set(1024, 1024);
  const c = key.shadow.camera;
  c.left = -14; c.right = 14; c.top = 14; c.bottom = -14;
  c.near = 1; c.far = 60;
  /* El sesgo evita el moaré de auto-sombra en la pared curva del cilindro;
     el normalBias hace el trabajo fino sin despegar la sombra de la base. */
  key.shadow.bias = -0.0008;
  key.shadow.normalBias = 0.05;
  key.shadow.radius = 3;
  l.push(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.05);
  fill.position.set(8, 3, 6);
  l.push(fill);
  /* Un toque por detras separa la taza del fondo, que es claro tambien. */
  const rim = new THREE.DirectionalLight(0xffffff, 0.18);
  rim.position.set(2, 6, -10);
  l.push(rim);
  return l;
}

/* ------------------------------------------------------------ init ------ */
function init(opts) {
  if (!opts || !opts.conteneur || !disponible()) return false;
  /* La malla se pide una vez y en paralelo: si llega, la escena se rehace con
     ella; si no llega, la taza de siempre ya está en pantalla. Nadie espera
     mirando un hueco gris a que baje un fichero de 2 Mo. */
  if (!modeloTaza && !modeloEnCurso) {
    cargarModeloTaza().then(m => { if (m && scene && groupe) rehacerTaza(); });
  }

  /* UN SOLO MOTOR, PASE LO QUE PASE.
     Esto se llamaba desde DOS sitios —el escenario de la taza y el panel de la
     escena— y cada llamada creaba otro canvas y otro renderer. El último
     ganaba: el motor dibujaba dentro del lienzo del panel, de 600x300 px y
     escondido, mientras el escenario enseñaba un lienzo huérfano al que ya no
     dibujaba nadie. Los mandos y las medidas estaban ahí —salen de este mismo
     módulo— y la taza no: exactamente lo que veía Franklin.
     Montado una vez, las siguientes llamadas MUDAN el lienzo al contenedor
     nuevo, que es lo que de verdad querían pedir. */
  if (ren && cv) {
    if (opts.peindre) peindre = opts.peindre;
    reubicar(opts.conteneur);
    return true;
  }

  conteneur = opts.conteneur;
  peindre = opts.peindre;

  cv = document.createElement('canvas');
  cv.className = 'apercu3d__toile';
  conteneur.appendChild(cv);
  observarCaja(conteneur);

  /* preserveDrawingBuffer OBLIGA al navegador a conservar el buffer despues de
     presentar cada cuadro, y en Safari de iOS eso desactiva optimizaciones y
     se nota MUCHO: era la causa principal de que la taza girara a trompicones
     en el iPhone. Se puso solo para poder leer pixeles en las pruebas, asi que
     ahora va detras de ?debug3d y en el telefono del cliente no existe.
     Para una captura puntual: rendre() y leer en el MISMO cuadro. */
  const debug = /[?&]debug3d/.test(location.search);
  /* El antialias vuelve tambien en movil. Lo habia quitado buscando fluidez,
     pero el freno de verdad era preserveDrawingBuffer: en un iPhone moderno el
     MSAA cuesta poco y es justo lo que hace que la silueta y el asa no se vean
     « basicas ». Se paga en el sitio correcto. */
  ren = new THREE.WebGLRenderer({ canvas: cv, antialias: true, alpha: true,
                                  preserveDrawingBuffer: debug,
                                  powerPreference: 'high-performance' });
  /* ══════════════════ CUÁNTOS PÍXELES SE DIBUJAN DE VERDAD ══════════════
     Estaba a 2, que en un lienzo de 694 × 490 son 1 360 000 píxeles por
     cuadro — y con la sombra, dos veces esa cifra. A 1,5 son 765 000: un 44 %
     menos de trabajo para una diferencia que en una taza sobre fondo liso no
     se ve. Mientras se ARRASTRA baja a 1, y al soltar vuelve a 1,5: el ojo no
     distingue nitidez en algo que se está moviendo, y sí distingue un tirón.
     Esto es exactamente lo que pidió: « durante interacción calidad media,
     cuando dejo de mover subir calidad ». */
  ren.setPixelRatio(dprQuieto());
  /* PCFSoft y no el básico: el borde duro de una sombra sin filtrar es de
     videojuego de hace veinte años, y aquí la sombra es justo lo que tiene
     que pasar desapercibido. */
  ren.shadowMap.enabled = true;
  ren.shadowMap.type = THREE.PCFSoftShadowMap;
  /* ══════════════════ LA SOMBRA NO SE REDIBUJA EN CADA CUADRO ═══════════
     Con `autoUpdate` —que es lo que trae Three de fábrica— CADA cuadro pinta
     la escena DOS veces: una para la pantalla y otra, entera, dentro del mapa
     de sombras. Son 78 000 triángulos pintados dos veces, 60 veces por
     segundo, para una sombra de contacto que en una taza que gira sobre su eje
     apenas cambia de forma.
     Aquí se dibuja cuando hace falta —al cargar, al cambiar el diseño, al
     cambiar de vista, al soltar el dedo— y no mientras se arrastra. */
  ren.shadowMap.autoUpdate = false;
  ren.shadowMap.needsUpdate = true;
  if ('outputColorSpace' in ren) ren.outputColorSpace = THREE.SRGBColorSpace;
  else if ('outputEncoding' in ren) ren.outputEncoding = THREE.sRGBEncoding;
  /* NADA DE ACES, Y ESTA MEDIDO. Puse el revelado filmico esperando un brillo
     mas fotografico y lo que hizo fue COMERSE EL COLOR del estampado: en la
     misma escena, la saturacion media del dibujo sobre la taza pasaba de 0,115
     a 0,048 — dos tercios perdidos — con la luminancia intacta, o sea lavado,
     no oscurecido. ACES esta pensado para escenas HDR; esta ya vive en rango
     bajo, asi que solo comprime lo que no hacia falta comprimir.
     Sin revelado y con el mismo esmalte nuevo: 0,114. La fidelidad vuelve y el
     realismo se queda. La referencia es 0,142, la del diseno plano; que la
     taza baje algo es CORRECTO — una superficie curva y brillante bajo luz de
     estudio no puede devolver el color plano de un PNG. */

  scene = new THREE.Scene();
  /* scene.environment lo aplica a TODOS los materiales sin tener que
     asignarles envMap uno a uno. */
  try { scene.environment = environnementStudio(ren); } catch (e) {}
  cam = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
  /* Más cerca de la altura de la taza. A 1,2 se miraba desde arriba lo
     bastante como para ver mucha boca, y eso estira la silueta: medido, la
     taza parecía 1,37 de alto por ancho cuando de verdad es 1,16. */
  cam.position.set(0, 0.7, 30);
  cam.lookAt(0, 0, 0);

  groupe = construireTasse();
  scene.add(groupe);
  brancherRehacer();
  scene.add(ombreContact());
  scene.add(planSombra());
  lumieres().forEach(x => scene.add(x));

  brancherGestes();
  redimensionner();
  addEventListener('resize', redimensionner);

  /* Nada de girar en bucle cuando el visor no se ve: ni fuera de pantalla ni
     con la pestana en segundo plano. */
  if ('IntersectionObserver' in window) {
    obs = new IntersectionObserver(e => {
      visible = e[0].isIntersecting;
      if (visible) demarrerBucle();
    }, { threshold: 0.05 });
    obs.observe(conteneur);
  }
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && actif) demarrerBucle();
  });

  majTexture(true);
  /* Puente de depuracion, como window.__studio: sin el, cualquier duda sobre
     la escena obliga a instrumentar el modulo a mano. No lo lee nadie en
     produccion. */
  /* EL PUENTE DEVUELVE LO QUE HAY AHORA, NO LO QUE HABÍA AL ARRANCAR.
     Era un objeto plano con una copia de las referencias, y `groupe` se
     sustituye cuando entra el modelo GLB o se rehace la taza: quien depurara
     desde la consola leía el grupo VIEJO —parado en 0— y concluía que la taza
     no giraba cuando estaba girando. Me pasó a mí midiendo esto. Con getters,
     lo que se lee es siempre lo que se está pintando. */
  window.__tasse3d = {
    get THREE() { return THREE; }, get ren() { return ren; },
    get scene() { return scene; }, get cam() { return cam; },
    get groupe() { return groupe; }, get deco() { return deco; },
    get texture() { return texture; }, get lienzo() { return lienzo; },
    get moviendo() { return moviendo; },
  };
  return true;
}

/* ENCUADRE. Franklin, sobre el aperçu en grande: « se ve algo pequeña ».
   Lo era: la distancia salia de un 30 partido por el aspecto, un numero
   heredado que no sabia como de grande es la taza. Con fov 32 y z=39 la altura
   visible eran 22 cm para una taza de 9,5 — el 42 % de la ventana, y el resto
   aire.
   Ahora se calcula: se pide que quepa la taza CON el asa, que es el caso
   ancho, y se toma la restriccion que mande segun la forma del hueco. El
   margen es del 12 %, suficiente para que no roce el borde al girar. */
const MARGE_CADRE = 1.12;
const CADRE_H = HAUTEUR * MARGE_CADRE;
/* Medio ancho con el asa: pared + radio del toro + su grosor. */
const CADRE_W = (R_EXT - 0.35 + 2.15 + 0.5) * 2 * MARGE_CADRE;

function distancePour(aspect) {
  const a = Math.max(aspect, 0.05);
  /* Altura visible necesaria: la que pide el alto, o la que pide el ancho
     traducida a altura por el aspecto. Gana la mayor. */
  const hVis = Math.max(CADRE_H, CADRE_W / a);
  const t = Math.tan(cam.fov * Math.PI / 360);
  /* Y LA PARED DE DELANTE NO ESTA EN EL EJE. La cuenta de encima encuadra un
     objeto PLANO a la distancia del centro; una taza tiene 4,1 cm de radio, y
     su cara frontal está justo esos 4,1 cm más cerca de la cámara, donde todo
     se ve un 28 % más grande. Con el hueco apaisado de la Vista 3D la taza
     salía cortada por arriba y por abajo. Se encuadra a la distancia de la
     superficie, no del eje. */
  return (hVis / 2) / t + R_EXT;
}

/* ============================================================================
   CAMBIAR EL ZOOM NO ES CAMBIAR EL TAMAÑO DEL LIENZO
   ----------------------------------------------------------------------------
   Franklin: « hago zoom y el sistema responde varios segundos después ».

   Estaba aquí, y es de libro: la rueda del ratón y el pellizco llamaban a
   `redimensionner()`, que entre otras cosas hace `ren.setSize(...)`. Y
   `setSize` escribe `canvas.width` y `canvas.height` — aunque el valor sea EL
   MISMO que ya tenían. Escribir esos dos atributos obliga al navegador a tirar
   el búfer de dibujo y pedirle otro a la GPU: es de las operaciones más caras
   que existen en WebGL, y un trackpad manda cien ruedas por segundo.

   El zoom sólo mueve la cámara. Eso es `ajustarCamara()`, que cuesta una
   multiplicación de matrices. El lienzo se redimensiona únicamente cuando el
   hueco cambia de verdad, y eso lo dice `observarCaja`, no un gesto.
   ========================================================================== */
let _lienzoW = 0, _lienzoH = 0;

function ajustarCamara() {
  if (!ren || !cam) return;
  const w = _lienzoW || (conteneur && conteneur.clientWidth) || 1;
  const h = _lienzoH || (conteneur && conteneur.clientHeight) || 1;
  cam.aspect = w / h;
  cam.position.z = distancePour(w / h) / zoom;
  cam.updateProjectionMatrix();
  marquer();
}

function redimensionner() {
  if (!ren || !conteneur) return;
  const w = conteneur.clientWidth, h = conteneur.clientHeight;
  /* UN LIENZO DE 2x2 PX NO SE ARREGLA SOLO, Y ERA ESTO.
     Si se pide el 3D con el contenedor todavía sin medir —la taza elegida
     desde otra pestaña, el panel aún plegado, la vista oculta— `clientWidth`
     vale 0, el renderer se quedaba en 2x2 px y NADIE lo volvía a medir al
     aparecer: el escenario salía vacío con los mandos y las medidas debajo,
     que es justo lo que veía Franklin. Medir cero no es medir: se deja como
     está y se espera a que el hueco exista de verdad (lo avisa `observarCaja`). */
  if (w < 8 || h < 8) return;
  /* Y si el hueco mide lo mismo que la última vez, NO se toca el lienzo: sólo
     la cámara. Es lo que convierte el zoom en instantáneo. */
  if (w !== _lienzoW || h !== _lienzoH) {
    _lienzoW = w; _lienzoH = h;
    ren.setSize(w, h, false);
    /* La sombra se ha quedado vieja con el lienzo nuevo. */
    if (ren.shadowMap) ren.shadowMap.needsUpdate = true;
  }
  ajustarCamara();
}

/* ============================================================================
   MIENTRAS SE MUEVE, RÁPIDO; CUANDO SE SUELTA, BONITO
   ----------------------------------------------------------------------------
   Un cuadro mientras se arrastra vale por lo que tarda, no por lo que enseña:
   nadie mira la nitidez de algo que está girando. Y en cuanto se suelta, lo
   contrario. Dos niveles, y el cambio ocurre solo:

     arrastrando  DPR 1 · sin refrescar el mapa de sombras
     quieto       DPR ≤1,5 · una pasada de sombra y a dormir

   `soltar()` espera 140 ms desde el último movimiento antes de subir la
   calidad: subirla en cada micro-pausa del dedo costaría más que dejarla baja.
   ========================================================================== */
const DPR_QUIETO = 1.5, DPR_MOVIENDO = 1;
const dprQuieto = () => Math.min(devicePixelRatio || 1, DPR_QUIETO);
let moviendo = false, _tempoQuieto = null, _nGestos = 0;
/* Los punteros que el lienzo tiene capturados AHORA. Se lleva la cuenta para
   poder soltarlos todos de golpe y para poder mirarlo desde `_diag`. */
const _capturados = new Set();
let _ultimoEvento = 0, _vigia = null, _reposar = null;

function soltarCaptura(id) {
  if (id == null) return;
  _capturados.delete(id);
  try { if (cv && cv.hasPointerCapture && cv.hasPointerCapture(id)) cv.releasePointerCapture(id); }
  catch (e) { /* ya no estaba: mejor */ }
}

function soltarTodo() {
  [..._capturados].forEach(soltarCaptura);
  _capturados.clear();
  if (typeof _reposar === 'function') _reposar();
}

function entrarEnMovimiento() {
  if (_tempoQuieto) { clearTimeout(_tempoQuieto); _tempoQuieto = null; }
  if (moviendo || !ren) return;
  moviendo = true; _nGestos++;
  const bajo = Math.min(devicePixelRatio || 1, DPR_MOVIENDO);
  /* Sin una medida buena del hueco no se toca nada: `setSize(0,0)` deja el
     lienzo en nada y no vuelve solo. */
  if (_lienzoW > 8 && ren.getPixelRatio() !== bajo) {
    ren.setPixelRatio(bajo); ren.setSize(_lienzoW, _lienzoH, false);
  }
}

function salirDelMovimiento() {
  if (_tempoQuieto) clearTimeout(_tempoQuieto);
  _tempoQuieto = setTimeout(() => {
    _tempoQuieto = null;
    if (!moviendo || !ren) return;
    moviendo = false;
    const alto = dprQuieto();
    if (_lienzoW > 8 && ren.getPixelRatio() !== alto) {
      ren.setPixelRatio(alto); ren.setSize(_lienzoW, _lienzoH, false);
    }
    if (ren.shadowMap) ren.shadowMap.needsUpdate = true;
    marquer();
  }, 140);
}

/* ---------------------------------------------------------- gestos ------ */
function brancherGestes() {
  let arrastrando = false, x0 = 0, y0 = 0, a0 = 0, e0 = 0;
  let pellizco = null;                 // { d0, zoom0 }
  const pos = ev => (ev.touches ? ev.touches[0] : ev);
  /* Los dedos que hay ahora mismo sobre el lienzo. Con eventos de puntero no
     existe `ev.touches`: el pellizco se arma con dos punteros vivos. */
  const dedos = new Map();
  const separacionPunteros = () => {
    const [a, b] = [...dedos.values()];
    return (a && b) ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  };
  const separacion = t => Math.hypot(t[0].clientX - t[1].clientX,
                                     t[0].clientY - t[1].clientY);

  const abajo = ev => {
    _ultimoEvento = performance.now();
    /* UN RATÓN ES UN SOLO PUNTERO, SIEMPRE. Y si alguna vez se pierde un
       `pointerup` —cambiar de pestaña a media pulsación, un menú del sistema,
       el navegador de una app— el dedo fantasma se queda en la lista para
       siempre: al siguiente toque habría « dos dedos », el visor entraría en
       modo pellizco y DEJARÍA DE GIRAR hasta recargar. Se vio probando.
       Un ratón limpia la lista al entrar, y más de dos punteros es siempre
       basura acumulada. */
    if (ev.pointerType === 'mouse' || dedos.size > 2) dedos.clear();
    if (ev.pointerId != null) dedos.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    entrarEnMovimiento();
    /* Dos dedos = acercar/alejar. La taza no se deforma ni se puede perder de
       vista: es la camara la que se aleja, entre topes. */
    const dosDedos = (ev.touches && ev.touches.length === 2) || dedos.size === 2;
    if (dosDedos) {
      arrastrando = false;
      pellizco = { d0: ev.touches ? separacion(ev.touches) : separacionPunteros(), zoom0: zoom };
      ev.preventDefault(); return;
    }
    arrastrando = true;
    const p = pos(ev); x0 = p.clientX; y0 = p.clientY; a0 = azimut; e0 = elevacion;
    ev.preventDefault();
  };

  const mueve = ev => {
    _ultimoEvento = performance.now();
    if (ev.pointerId != null && dedos.has(ev.pointerId))
      dedos.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    const dosDedos = (pellizco && ev.touches && ev.touches.length === 2)
                  || (pellizco && dedos.size === 2);
    if (dosDedos) {
      const d = ev.touches ? separacion(ev.touches) : separacionPunteros();
      if (pellizco.d0 > 0 && d > 0) {
        zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, pellizco.zoom0 * (d / pellizco.d0)));
        /* Sólo la cámara. Antes esto realojaba el búfer de dibujo en CADA
           movimiento de dedo: ver la nota de `ajustarCamara`. */
        ajustarCamara();
      }
      ev.preventDefault(); return;
    }
    if (!arrastrando) return;
    entrarEnMovimiento();
    const p = pos(ev);
    /* EL ÚLTIMO VALOR MANDA, Y NO HAY COLA. El ángulo se calcula SIEMPRE
       desde donde empezó el gesto (`a0` + lo que se ha recorrido), no sumando
       incrementos. Así, si llegan cincuenta movimientos mientras el navegador
       está ocupado, el cuadro que salga dibuja el ÚLTIMO — los cuarenta y
       nueve anteriores no se « ejecutan con retraso », se quedan sin pintar,
       que es justo lo que se quiere. Pintar uno por cuadro lo hace `marquer`,
       que sólo apunta que hay algo que dibujar. */
    azimut = a0 + (p.clientX - x0) * 0.011;
    /* La vertical se limita: la gracia es mirar la taza, no ponerla del reves
       ni verla desde el fondo del vaso. */
    elevacion = Math.min(ELEV_MAX, Math.max(ELEV_MIN, e0 + (p.clientY - y0) * 0.006));
    marquer();
    ev.preventDefault();
  };

  /* Lo que `soltarTodo()` necesita poder hacer desde fuera: volver al reposo.
     Se registra aquí porque el estado del gesto vive en esta función. */
  _reposar = () => {
    arrastrando = false; pellizco = null; dedos.clear();
    salirDelMovimiento();
  };

  /* EL VIGÍA. Dos segundos sin un solo evento con el gesto « vivo » significa
     que se perdió el `pointerup`. Se suelta todo antes de que el cliente lo
     note. Corre una vez por segundo y sólo mientras hay algo que vigilar. */
  const vigilar = () => {
    if (!moviendo && !dedos.size && !arrastrando) return;
    if (performance.now() - _ultimoEvento > 2000) soltarTodo();
  };
  if (!_vigia) _vigia = setInterval(vigilar, 1000);

  const arriba = ev => {
    if (ev && ev.pointerId != null) dedos.delete(ev.pointerId);
    if (dedos.size < 2) pellizco = null;
    if (!dedos.size) arrastrando = false;
    if (!ev || ev.pointerId == null) { arrastrando = false; pellizco = null; dedos.clear(); }
    salirDelMovimiento();
  };

  /* Doble clic = volver al frente. Es lo que hace todo configurador de
     producto, y aquí hacía falta más que en ninguno: girando la taza a mano se
     pierde el centro del diseño, que es justo lo que se viene a mirar. */
  cv.addEventListener('dblclick', ev => { ev.preventDefault(); verDesde('frente', true); });

  /* En escritorio el equivalente natural es la rueda. */
  cv.addEventListener('wheel', ev => {
    zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoom * (ev.deltaY > 0 ? 0.92 : 1.08)));
    /* `ajustarCamara`, NO `redimensionner`: la rueda no cambia el tamaño del
       hueco, y realojar el búfer de dibujo cien veces por segundo era el
       motivo de que el zoom fuera a tirones. */
    entrarEnMovimiento(); ajustarCamara(); salirDelMovimiento();
    ev.preventDefault();
  }, { passive:false });

  /* ══════════════ UN SOLO CAMINO DE ENTRADA, Y QUE NO SE PIERDA ═════════
     Estaban enganchados `mouse*` Y `touch*` a la vez. En una pantalla táctil
     el navegador manda los dos —el táctil de verdad y un ratón simulado
     detrás—, así que cada dedo movía la taza DOS veces y el segundo cálculo
     partía de un punto de origen que ya no era el suyo: de ahí parte de los
     saltos. Con eventos de puntero hay un solo camino para dedo, ratón y
     lápiz.

     Y `setPointerCapture`: el arrastre sigue funcionando aunque el dedo se
     salga del lienzo, y el navegador garantiza el `pointerup` — antes, soltar
     fuera de la ventana dejaba la taza pegada al ratón.

     `pointermove` va en el LIENZO, no en `window`: así no se ejecuta un
     manejador por cada movimiento del ratón en toda la página. */
  if (window.PointerEvent) {
    cv.style.touchAction = 'none';
    cv.addEventListener('pointerdown', ev => {
      try { cv.setPointerCapture(ev.pointerId); _capturados.add(ev.pointerId); } catch (e) {}
      abajo(ev);
    }, { passive:false });
    cv.addEventListener('pointermove', mueve, { passive:false });
    ['pointerup', 'pointercancel'].forEach(t => cv.addEventListener(t, ev => {
      soltarCaptura(ev.pointerId);
      arriba(ev);
    }));

    /* ══════════ LA RED DE SEGURIDAD, Y NO ES TEÓRICA ══════════════════════
       `setPointerCapture` manda TODOS los eventos de ese puntero al lienzo
       hasta que se suelta. Si por lo que sea el `pointerup` no llega —el
       navegador pierde el puntero, una capa se mete por medio, el sistema
       abre un menú— la captura se queda puesta y a partir de ahí **ningún
       clic del resto de la página llega a su botón**: la interfaz parece
       congelada aunque todo siga funcionando por dentro. Es exactamente lo
       que describía Franklin: « la vista queda cargada pero la UI se queda
       pillada, no puedo tocar casi nada ».

       Tres cierres, por si acaso:
         · el `pointerup` del lienzo (el camino normal);
         · el de la ventana, que se entera aunque el del lienzo no salte;
         · y un vigía: si dice que se está moviendo y hace dos segundos que no
           llega un solo evento, se suelta todo y se vuelve al reposo. */
    ['pointerup', 'pointercancel', 'blur'].forEach(t =>
      window.addEventListener(t, ev => {
        if (ev && ev.pointerId != null) { soltarCaptura(ev.pointerId); arriba(ev); }
        else soltarTodo();
      }));
    document.addEventListener('visibilitychange', () => { if (document.hidden) soltarTodo(); });
  } else {
    ['mousedown', 'touchstart'].forEach(t => cv.addEventListener(t, abajo, { passive:false }));
    ['mousemove', 'touchmove'].forEach(t => window.addEventListener(t, mueve, { passive:false }));
    ['mouseup', 'touchend', 'touchcancel'].forEach(t => window.addEventListener(t, arriba));
  }
}

/* ============================================================================
   EL PERFILADOR: NÚMEROS DE SU MÁQUINA, NO DE LA MÍA
   ----------------------------------------------------------------------------
   Franklin: « no quiero adivinar, quiero identificar qué operación está
   causando el retraso ». Y hace falta que lo mida ÉL: aquí el panel del
   navegador va oculto, y un navegador oculto congela `requestAnimationFrame`
   a uno o dos cuadros por segundo — cualquier medida tomada así diría que
   todo va lentísimo aunque fuera perfecto.

   Se usa desde la consola del Studio:

       await TASSE3D.perfilar(10000)     ← arrastra la taza durante esos 10 s

   Devuelve, de su propio ordenador: cuadros por segundo, el cuadro peor, el
   percentil 95, cuántos renders se pidieron, cuántos eventos de puntero
   entraron, triángulos, llamadas de dibujo, tamaño de las texturas y si el
   navegador estaba estrangulando el bucle. Con eso se sabe si el problema es
   la GPU, la geometría, la textura o simplemente un `requestAnimationFrame`
   que el sistema no deja correr.
   ========================================================================== */
function perfilar(durada) {
  const ms = Math.max(1000, +durada || 8000);
  return new Promise(resolver => {
    const cuadros = [];
    const r0 = _nRenders, g0 = _nGestos;
    let eventos = 0;
    const contar = () => { eventos++; };
    if (cv) ['pointermove', 'mousemove', 'touchmove', 'wheel']
      .forEach(t => cv.addEventListener(t, contar, { passive: true }));

    let ultimo = performance.now();
    const fin = ultimo + ms;
    const paso = (t) => {
      cuadros.push(t - ultimo); ultimo = t;
      /* Se le pide un cuadro al visor en cada vuelta: así se mide el coste
         real de dibujar, no el de un bucle vacío. */
      marquer();
      if (t < fin) requestAnimationFrame(paso);
      else {
        if (cv) ['pointermove', 'mousemove', 'touchmove', 'wheel']
          .forEach(x => cv.removeEventListener(x, contar));
        const v = cuadros.slice(1).sort((a, b) => a - b);
        const pct = q => v.length ? +v[Math.min(v.length - 1, Math.floor(v.length * q))].toFixed(1) : null;
        const media = v.length ? v.reduce((a, b) => a + b, 0) / v.length : 0;
        let tri = 0;
        if (scene) scene.traverse(n => {
          if (!n.isMesh || !n.geometry) return;
          const g = n.geometry;
          tri += g.index ? g.index.count / 3 : (g.attributes.position ? g.attributes.position.count / 3 : 0);
        });
        resolver({
          segundos: +(ms / 1000).toFixed(1),
          fps: media ? Math.round(1000 / media) : 0,
          cuadro_medio_ms: +media.toFixed(1),
          cuadro_p95_ms: pct(0.95),
          cuadro_peor_ms: v.length ? +v[v.length - 1].toFixed(1) : null,
          renders: _nRenders - r0,
          gestos: _nGestos - g0,
          eventos_puntero: eventos,
          eventos_por_segundo: Math.round(eventos / (ms / 1000)),
          triangulos: Math.round(tri),
          draw_calls: ren ? ren.info.render.calls : null,
          texturas_en_memoria: ren ? ren.info.memory.textures : null,
          textura_diseno: lienzo ? lienzo.width + '×' + lienzo.height : null,
          dpr_ahora: ren ? ren.getPixelRatio() : null,
          lienzo_px: ren ? ren.domElement.width + '×' + ren.domElement.height : null,
          sombra_auto: ren ? ren.shadowMap.autoUpdate : null,
          /* Si esto sale muy por debajo de 60 con la pestaña delante, el
             navegador está estrangulando el bucle y el problema NO es el 3D. */
          bucle_estrangulado: media > 100,
          pestana_oculta: document.hidden,
        });
      }
    };
    requestAnimationFrame(paso);
  });
}

/* ============================================================================
   LA HOJA DE CONTACTO: VER LA TAZA BLANCA, SIN DISEÑO, DESDE CINCO SITIOS
   ----------------------------------------------------------------------------
   Franklin: « antes de volver a integrar UV, quiero que me muestres primero la
   taza COMPLETAMENTE BLANCA Y SIN DISEÑO… frente, perfil, asa izquierda, asa
   derecha y ligeramente desde arriba ». Esto es eso, en un solo golpe:

       TASSE3D.hojaDeContacto()      abre las cinco vistas en otra pestaña

   Quita la funda del estampado, gira el modelo a cada ángulo, lo dibuja y
   compone una tira. No toca el estado del visor: al acabar lo deja como
   estaba.
   ========================================================================== */
function hojaDeContacto(lado) {
  if (!ren || !groupe) return null;
  const S = lado || 300;
  const eraDeco = deco ? deco.visible : null;
  if (deco) deco.visible = false;
  const pr = ren.getPixelRatio(), w0 = ren.domElement.width, h0 = ren.domElement.height;
  const ry = groupe.rotation.y, rx = groupe.rotation.x;
  const vistas = [['Frente', Math.PI / 2, 0], ['Asa izquierda', Math.PI, 0],
                  ['Asa derecha', 0, 0], ['Desde arriba', Math.PI / 2, 0.45],
                  ['La base', Math.PI / 2, -0.55]];
  const tiras = [];
  try {
    ren.setPixelRatio(1); ren.setSize(S, S, false);
    cam.aspect = 1; cam.updateProjectionMatrix();
    for (const [, az, el] of vistas) {
      groupe.rotation.y = az; groupe.rotation.x = el;
      ren.render(scene, cam);
      tiras.push(ren.domElement.toDataURL('image/png'));
    }
  } finally {
    ren.setPixelRatio(pr); ren.setSize(w0 / pr, h0 / pr, false);
    groupe.rotation.y = ry; groupe.rotation.x = rx;
    if (deco && eraDeco !== null) deco.visible = eraDeco;
    redimensionner(); marquer();
  }
  const html = '<!doctype html><meta charset="utf-8"><title>Taza blanca — geometría</title>'
    + '<style>body{margin:0;background:#f7f5f1;font:13px system-ui;text-align:center}'
    + 'figure{display:inline-block;margin:8px}img{display:block;border:1px solid #e3ded6}'
    + 'figcaption{padding-top:6px;color:#333}</style>'
    + '<h3 style="font-weight:600">Taza blanca, sin diseño — Ø 8,2 × 9,5 cm</h3>'
    + tiras.map((u, i) => `<figure><img src="${u}" width="${S}"><figcaption>${vistas[i][0]}</figcaption></figure>`).join('');
  const v = window.open('', '_blank');
  if (v) { v.document.write(html); v.document.close(); }
  return tiras.length;
}

/* ---------------------------------------------------------- textura ----- */
/* El diseno plano se vuelca en la funda. Con debounce corto: arrastrar una
   foto dispara decenas de cambios por segundo y repintar la textura en cada
   uno no aporta nada que el ojo vea. */
function majTexture(inmediato) {
  if (!texture || !peindre) return;
  clearTimeout(tempo);
  const hacer = () => {
    try { peindre(lienzo); } catch (e) { return; }
    texture.needsUpdate = true;
    marquer();
  };
  if (inmediato) hacer(); else tempo = setTimeout(hacer, DEBOUNCE);
}

/* -------------------------------------------------------- bucle --------- */
/* Render bajo demanda con una cola corta de cuadros: al soltar el dedo no
   hace falta seguir dibujando 60 veces por segundo para siempre. */
let cuadrosPendientes = 0, _nRenders = 0;
function marquer() { cuadrosPendientes = 2; demarrerBucle(); }

/* Un cuadro, ahora, sin pasar por el bucle. El bucle vive de
   requestAnimationFrame, que el navegador CONGELA en una pestana de fondo —
   correcto para la bateria, pero deja sin salida los casos en que hace falta
   una imagen puntual: una miniatura para el carrito, una captura, o
   simplemente comprobar el render. */
function rendre() {
  if (!ren || !groupe) return false;
  groupe.rotation.y = azimut;
  groupe.rotation.x = elevacion * 0.55;
  ren.render(scene, cam);
  _nRenders++;
  return true;
}

/* Instantanea de la taza tal como se ve ahora. Se dibuja y se lee EN EL MISMO
   cuadro: sin eso el navegador ya ha presentado el buffer y toDataURL devuelve
   vacio — que es justo por lo que existia preserveDrawingBuffer, y ese se
   quito porque frenaba el iPhone.

   Para que valga como imagen de carrito, se rinde a mayor resolucion durante
   un instante y se devuelve el tamano normal despues. */
function instantane(cote) {
  if (!ren || !groupe) return null;
  const c = cote || 900;
  const w0 = ren.domElement.width, h0 = ren.domElement.height;
  const pr0 = ren.getPixelRatio();
  try {
    ren.setPixelRatio(1);
    ren.setSize(c, c, false);
    cam.aspect = 1; cam.updateProjectionMatrix();
    rendre();
    return ren.domElement.toDataURL('image/png');
  } catch (e) {
    return null;
  } finally {
    ren.setPixelRatio(pr0);
    ren.setSize(w0 / pr0, h0 / pr0, false);
    redimensionner();
  }
}

/* ============================================================================
   UN CUADRO, PEDIDO POR LOS DOS CAMINOS
   ----------------------------------------------------------------------------
   Esto explica los « tres o cuatro segundos » mejor que ninguna otra cosa que
   he medido. En un documento que el navegador considera no visible —una
   ventana tapada por otra, una pestaña en segundo plano, ciertos modos de
   ahorro de energía— Chrome **estrangula `requestAnimationFrame` a 1 o 2
   llamadas por segundo**, pero deja los temporizadores corriendo. Medido en
   esta misma página, a la vez:

       requestAnimationFrame  →   2 por segundo
       setTimeout(…, 16)      →  59 por segundo

   Con el visor colgado sólo de `rAF`, eso son dos cuadros por segundo: se
   arrastra la taza, no pasa nada, y medio segundo después salta. Exactamente
   lo que él describe.

   Así que cada cuadro se pide por los DOS caminos y gana el que llegue antes.
   Cuando el navegador va fino, gana `rAF` y todo es como debe ser —sincronizado
   con la pantalla—; cuando lo estrangula, el temporizador salva la interacción.
   Y cuando la página está de verdad oculta, el bucle ya se para solo unas
   líneas más abajo, así que esto no gasta batería a escondidas.
   ========================================================================== */
function pedirCuadro(cb) {
  let hecho = false;
  const una = () => { if (hecho) return; hecho = true; cb(); };
  const r = requestAnimationFrame(una);
  const t = setTimeout(una, 20);
  return { r, t };
}

function demarrerBucle() {
  if (bucle || !actif || !visible || document.hidden) return;
  const paso = () => {
    if (!actif || !visible || document.hidden) { bucle = null; return; }
    if (cuadrosPendientes > 0) { cuadrosPendientes--; rendre(); bucle = pedirCuadro(paso); }
    /* Sin nada pendiente, PARAR. Antes seguia pidiendo un callback por cuadro
       para no dibujar nada: 60 despertares por segundo de balde, con la CPU
       del telefono caliente y el resto de la interfaz peleando por el hilo. */
    else bucle = null;
  };
  bucle = pedirCuadro(paso);
}

function activer(on) {
  actif = !!on && !!ren;
  if (actif) { redimensionner(); majTexture(true); demarrerBucle(); }
  else if (bucle) {
    /* El cuadro se pidió por los dos caminos: se cancelan los dos. */
    if (bucle.r != null) cancelAnimationFrame(bucle.r);
    if (bucle.t != null) clearTimeout(bucle.t);
    bucle = null;
  }
}

/* ============================================================================
   LA CAMARA, A SITIOS CONCRETOS
   ----------------------------------------------------------------------------
   Franklin: « botones rápidos: asa izquierda, frente, asa derecha — pero esos
   botones deben cambiar la posicion de la camara 3D, no generar imagenes
   diferentes ». Eso es exactamente lo que hacen: mueven `azimut`, que es la
   rotacion del grupo, y el render siguiente sale de la misma escena.

   El asa esta modelada en +X del grupo. Con azimut = 0 ese +X mira al eje +X
   del mundo, o sea a la derecha de la pantalla; con azimut = pi queda a la
   izquierda; y con azimut = pi/2 se va detras y enfrente queda el centro del
   estampado, que es la vista de partida.

   Se llega con una animacion corta, no de un salto: en un objeto 3D el salto
   hace perder de vista por donde iba la taza. 260 ms, que es lo que tarda el
   ojo en seguir un giro sin marearse.
   ========================================================================== */
const CAMARAS = {
  'asa-der':  0,
  'frente':   Math.PI / 2,
  'asa-izq':  Math.PI,
  'atras':    -Math.PI / 2,
};

let animCam = null;
function verDesde(clave, animar) {
  const destino = (typeof clave === 'number') ? clave : CAMARAS[clave];
  if (destino == null) return;
  if (animCam) {
    /* Se pidió por dos caminos: se cancelan los dos. */
    if (animCam.r != null) cancelAnimationFrame(animCam.r);
    if (animCam.t != null) clearTimeout(animCam.t);
    animCam = null;
  }
  /* Por el camino corto: sin esto, ir de « asa derecha » a « asa izquierda »
     podia dar una vuelta y media segun donde estuviera el arrastre anterior. */
  let a0 = azimut;
  let d = destino - a0;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  const e0 = elevacion, eD = 0.12 - e0;
  if (!animar) { azimut = a0 + d; elevacion = 0.12; marquer(); return; }
  const t0 = performance.now(), DUR = 260;
  const paso = () => {
    const t = Math.min(1, (performance.now() - t0) / DUR);
    const k = t < 0.5 ? 2 * t * t : 1 - 2 * (1 - t) * (1 - t);   // suave a los dos lados
    azimut = a0 + d * k;
    elevacion = e0 + eD * k;
    marquer();
    animCam = t < 1 ? pedirCuadro(paso) : null;
  };
  animCam = pedirCuadro(paso);
}

function fijarZoom(v) {
  zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +v || 1));
  /* Los botones « − » y « + » tampoco cambian el tamaño del lienzo. */
  ajustarCamara();
}

/**
 * Lo que se ve AHORA, en centimetros de taza. Nada de esto es decorativo:
 * es lo que Franklin necesita comprobar antes de mandar a imprimir — cuanto
 * diseno se ve de frente, cuanto queda sin imprimir junto al asa, y si el
 * estampado esta centrado respecto al asa.
 */
function medidas() {
  const D = cam ? cam.position.length() : 24;
  /* Mitad del arco que la camara puede ver de un cilindro de radio R a
     distancia D. Mas alla de ese angulo la superficie se va de canto. */
  const medioVisible = Math.acos(Math.min(0.999, R_EXT / D));
  /* Angulo del punto que mira a la camara, en el sistema del cilindro donde
     el centro del wrap esta en 0 (el wrap va centrado enfrente del asa). */
  let centro = Math.PI / 2 - azimut;
  while (centro > Math.PI) centro -= 2 * Math.PI;
  while (centro < -Math.PI) centro += 2 * Math.PI;
  const medioWrap = ARC_WRAP / 2;
  const a = Math.max(centro - medioVisible, -medioWrap);
  const b = Math.min(centro + medioVisible, medioWrap);
  const arcoVisto = Math.max(0, b - a);
  const cmPorRad = R_EXT;                       // longitud de arco = R × ángulo
  return {
    wrap: { w: wrapW, h: wrapH },
    circunferencia: +CIRCONFERENCE.toFixed(1),
    sinImprimir: +(CIRCONFERENCE - wrapW).toFixed(1),
    margenAsa: +((CIRCONFERENCE - wrapW) / 2).toFixed(1),
    visibleCm: +(arcoVisto * cmPorRad).toFixed(1),
    visiblePc: Math.round(arcoVisto * cmPorRad / wrapW * 100),
    grados: Math.round(((centro * 180 / Math.PI) % 360 + 360) % 360),
    zoom: +zoom.toFixed(2),
  };
}

/**
 * Mover el lienzo a otro sitio de la pagina sin rehacer el renderer.
 * La « Vista 3D » y la escena del mockup enseñan LA MISMA taza: crear un
 * segundo WebGL para la segunda seria pagar dos veces por lo mismo y arriesgar
 * que las dos digan cosas distintas.
 */
function reubicar(nuevo) {
  if (!cv || !nuevo) return false;
  if (nuevo !== conteneur || cv.parentElement !== nuevo) {
    conteneur = nuevo;
    nuevo.appendChild(cv);
  }
  observarCaja(nuevo);
  redimensionner();
  marquer();
  return true;
}

/* EL HUECO AVISA CUANDO EXISTE. Un ResizeObserver sobre el contenedor cubre de
   una vez todos los casos en que el lienzo se quedaba con el tamaño de antes:
   cambiar de pestaña, plegar un panel, abrir y cerrar el mockup, girar el
   teléfono o mover el lienzo de un sitio a otro. Antes había que acordarse de
   llamar a `redimensionner()` en cada uno de esos sitios — y bastaba con
   olvidarse de uno para que la taza desapareciera. */
let obsCaja = null;
function observarCaja(el2) {
  if (typeof ResizeObserver === 'undefined' || !el2) return;
  if (obsCaja) obsCaja.disconnect();
  obsCaja = new ResizeObserver(() => {
    if (!ren || !conteneur) return;
    if (conteneur.clientWidth < 8 || conteneur.clientHeight < 8) return;
    const w = Math.round(conteneur.clientWidth * Math.min(devicePixelRatio || 1, 2));
    if (Math.abs(cv.width - w) < 2) return;      // ya está a medida
    redimensionner();
    marquer();
  });
  obsCaja.observe(el2);
}

/* Cambiar la taza del torno por la malla en caliente: se tira el grupo viejo
   y se construye otro con la textura ya pintada, sin tocar cámara ni ángulo,
   así que el cambio se ve como un detalle que mejora, no como un salto. */
function rehacerTaza() {
  if (!scene || !groupe) return;
  const viejo = groupe;
  groupe = construireTasse();
  groupe.rotation.copy(viejo.rotation);
  scene.add(groupe);
  scene.remove(viejo);
  viejo.traverse(n2 => {
    if (n2.isMesh && n2.geometry && n2 !== deco) n2.geometry.dispose();
  });
  majTexture(true);
  redimensionner();
  marquer();
}
function brancherRehacer() { /* punto de enganche, por claridad del flujo */ }

/* Diagnostico: el render va por demanda y con varias condiciones (activo,
   visible, pestana en primer plano). Cuando no se ve nada, esto dice cual de
   ellas esta cortando el paso, sin tener que instrumentar a mano. */
function _diag() {
  return { actif, visible, oculto: document.hidden, bucle: !!bucle,
           cuadrosPendientes, renders: _nRenders,
           /* Si esto no está vacío con el dedo levantado, ahí está el
              « no puedo tocar nada »: el lienzo se quedó con la captura. */
           punterosCapturados: [..._capturados], moviendo,
           cam: cam && { z: +cam.position.z.toFixed(1), aspect: +cam.aspect.toFixed(2), zoom },
           textura: TEX_W + 'x' + TEX_H, movil: esMovil(),
           hijos: scene && scene.children.length,
           lienzo: lienzo && (lienzo.width + 'x' + lienzo.height),
           deco: deco && { visible: deco.visible,
             enGrupo: !!(groupe && groupe.children.indexOf(deco) >= 0),
             conMapa: !!(deco.material && deco.material.map),
             vertices: deco.geometry.attributes.position.count,
             arcDeg: Math.round(ARC_WRAP * 180 / Math.PI),
             trouDeg: Math.round(360 - ARC_WRAP * 180 / Math.PI) } };
}

return { disponible, chargerThree, init, activer, majTexture, redimensionner,
         rendre, instantane, _diag,
         fijarWrap, wrap: () => ({ w: wrapW, h: wrapH }),
         verDesde, fijarZoom, medidas, reubicar, montado: () => !!ren, perfilar,
         hojaDeContacto,
         ajustarCamara,
         fijarModelo, cargarModeloTaza, conModelo: () => !!modeloTaza,
         radioModelo: () => modeloRadio, anguloAsaModelo: () => modeloAngulo,
         zonaImprimible, medidasModelo: () => (modeloTaza ? modeloTaza.userData.medidas : null),
         radioEnvolvente: () => modeloRadioMax,
         medidaLienzo: () => (cv ? { w: cv.width, h: cv.height } : null),
         WRAP_CM, TEX_W, TEX_H };
})();
